import { useEffect, useReducer, useContext } from "react";
import InstitutionRegionContext from "./InstitutionRegionContext";
import "./Lightcast.scss";
import { sendRequest } from "../../common/http";
import { AppContext, AppContextInterface } from "../../contexts/AppContext";
import translate from "@/i18n/translate";
import { WmButton } from "@watermarkinsights/ripple-react";
import Trends from "./Trends";
import ChangeInPopulation from "./population/ChangeInPopulation";
import LaborForce from "./LaborForce";
import Industries from "./industries/Industries";
import Occupations from "./occupations/Occupations";
import LightcastFooter from "./LightcastFooter";
import { getLoaderStatus } from "../common/helper";
import {
  LaborForceBreakdown,
  defaultLaborForceBreakdown,
  RegionalContextInterface,
  RegionalContext,
  defaultRegionalContext
} from "./Interface";

interface LightcastLoadedComponents {
  regionalContext: boolean;
  populationByRace: boolean;
  populationByAge: boolean;
  occupations: boolean;
  industries: boolean;
  trendsAndLaborforce: boolean;
}

interface LightcastState {
  regionalContext: RegionalContext | null;
  showLightcastWidget: Boolean;
  laborForceBreakdown: LaborForceBreakdown | null;
  loadedComponents: LightcastLoadedComponents;
  laborForceBreakdownLoader: Boolean;
  lfbApiError: Boolean;
}

interface LightcastAction {
  type: string;
  contextResult: RegionalContextInterface;
  lfb: LaborForceBreakdown;
  component: string;
  lfbApiError: boolean;
}

const defaultContextResult = {
  regionalContext: defaultRegionalContext,
  showLightcastWidget: false
};

const defaultLightcastAction = {
  type: "",
  contextResult: defaultContextResult,
  lfb: defaultLaborForceBreakdown,
  component: "",
  lfbApiError: false
};

const reducer = (state: LightcastState, action: LightcastAction) => {
  switch (action.type) {
    case "setRegionalContext":
      return {
        ...state,
        regionalContext: action.contextResult.regionalContext,
        showLightcastWidget: action.contextResult.showLightcastWidget
      };

    case "setLaborForceBreakdown":
      return {
        ...state,
        laborForceBreakdown: action.lfb,
        laborForceBreakdownLoader: false
      };
    case "setApiError":
      return {
        ...state,
        lfbApiError: action.lfbApiError
      };
    case "setLoadedComponent":
      const component = action.component;
      let loadedComponents = state.loadedComponents;
      loadedComponents[component] = true;
      return { ...state, loadedComponents: loadedComponents };
    default:
      return state;
  }
};

const allComponentsLoaded = (loadedComponents: LightcastLoadedComponents) => {
  return (
    loadedComponents.regionalContext &&
    loadedComponents.populationByRace &&
    loadedComponents.populationByAge &&
    loadedComponents.occupations &&
    loadedComponents.industries &&
    loadedComponents.trendsAndLaborforce
  );
};

const initialState = {
  regionalContext: null,
  showLightcastWidget: false,
  laborForceBreakdown: null,
  loadedComponents: {
    regionalContext: false,
    populationByRace: false,
    populationByAge: false,
    occupations: false,
    industries: false,
    trendsAndLaborforce: false
  },
  laborForceBreakdownLoader: true,
  lfbApiError: false
};

interface lightcastProp {
  setLightcastTab: () => void;
}

export default function Lightcast({ setLightcastTab }: lightcastProp) {
  const { setComponentLoaded, institution } =
    useContext<AppContextInterface>(AppContext);
  const [state, dispatch] = useReducer(reducer, initialState);

  const openPreviewModal = () => {
    setComponentLoaded("openPreviewModal");
  };

  const openlaborForceHtmlViewLink = () => {
    return window.open(getURL(), "_blank");
  };
  const getURL = () => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale");
    const isPlanning = window.location.pathname.includes("/planning/");
    const base = isPlanning ? `/insights/planning` : `/insights`;
    let url = `${base}/institution/${institution?.platformUuid ?? ""}/labor_market/html`;
    if (locale !== null) {
      url = `${url}?locale=${locale}`;
    }

    return url;
  };

  useEffect(() => {
    const applyResult = (result: RegionalContextInterface) => {
      if (result.showLightcastWidget) setLightcastTab();

      dispatch({
        ...defaultLightcastAction,
        type: "setRegionalContext",
        contextResult: result
      });

      onComponentLoad("regionalContext");
    };

    sendRequest(
      { uri: "/lightcast/regional_context", camelize: true },
      applyResult
    );
  }, []);

  const setLaborForceBreakdown = (lfb: LaborForceBreakdown | null) => {
    lfb &&
      dispatch({
        ...defaultLightcastAction,
        type: "setLaborForceBreakdown",
        lfb: lfb
      });
  };

  const setApiError = (lfbApiError: boolean) => {
    dispatch({
      ...defaultLightcastAction,
      type: "setApiError",
      lfbApiError: lfbApiError
    });
  };

  useEffect(() => {
    const liveRegion = document.querySelector(".lightcast-status");
    getLoaderStatus(!allComponentsLoaded(state.loadedComponents), liveRegion);
  }, [allComponentsLoaded(state.loadedComponents)]);

  const onComponentLoad = (component: string) => {
    dispatch({
      ...defaultLightcastAction,
      type: "setLoadedComponent",
      component: component
    });
  };

  return (
    <section
      className="body tab-body-home"
      id="lightcast-insights"
    >
      <section className="insights">
        <div className="institution-name lightcast-header">
          <span
            className="sr-only lightcast-status"
            role="status"
            aria-live="assertive"
          ></span>
          <h3 className="helper-label helper-default-margin">
            {translate("app.lightcast.regionLabel")}
          </h3>
          <div className="lightcast-header-content">
            <div className="helper-text">
              {translate("app.lightcast.helperDescription")} {" "}
              <WmButton id="lightcast-learn-more" buttonType="textonly" onClick={openPreviewModal}>
                {translate("app.learnMore")}
              </WmButton>
            </div>
            <WmButton
              button-type="secondary"
              icon="f3cc"
              icon-size="14px"
              onClick={() => openlaborForceHtmlViewLink()}
            >
              {translate("app.lightcast.shareInsights")}
            </WmButton>
          </div>
        </div>
        {state.showLightcastWidget ? (
          <>
            <div className="wm-grid">
              <div className="wm-grid-row">
                <InstitutionRegionContext
                  regionalContext={state.regionalContext}
                  onComponentLoad={onComponentLoad}
                />

                <div className="grid-container wm-grid-col-12 wm-grid-col-lg-12 wm-grid-col-md-10 wm-grid-col-sm-8">
                  <Trends
                    setLaborForceBreakdown={setLaborForceBreakdown}
                    onComponentLoad={onComponentLoad}
                    setApiError={setApiError}
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="grid-container change-in-population">
                <ChangeInPopulation onComponentLoad={onComponentLoad} />
              </div>
            </div>
            <div className="wm-grid-row">
              <div className="grid-container wm-grid-col-12 wm-grid-col-xl-12 wm-grid-col-lg-12 wm-grid-col-md-10 wm-grid-col-sm-8">
                <LaborForce
                  laborForceBreakdown={state.laborForceBreakdown}
                  laborForceBreakdownLoader={state.laborForceBreakdownLoader}
                  lfbApiError={state.lfbApiError}
                />
              </div>
            </div>
            <div className="wm-grid">
              <div className="wm-grid-row">
                <Industries onComponentLoad={onComponentLoad} />
                <Occupations onComponentLoad={onComponentLoad} />
                <LightcastFooter />
              </div>
            </div>
          </>
        ) : (
          <div className="grid-container-empty wm-grid-col wm-grid-col-lg-6 wm-grid-col-md-10 -min-width" />
        )}
      </section>
    </section>
  );
}
